import { T, useGT } from 'gt-react';
import { ChartLineUpIcon } from '@phosphor-icons/react';
import { reportNumberFormatOptions } from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { defineComponentType } from '../registry';
import { NumberFormatFields } from '../format/NumberFormatFields';
import { CategorySortFields } from './CategorySortFields';
import type { LineChartComponent } from '../types';
import {
  CategoryValueFields,
  ConfigRow,
  ToggleRow,
  valueByCategoryTitle,
  ChartFrame,
  useXYChart,
  useChartFit,
  ConfigSelectTrigger,
} from './shared';
import { aggregateByCategory, sortCategories, inAxisOrder } from './chartRows';
import { downsampleSeries, plotWidth, showsPointMarkers } from './density';
import { BuiltinChart } from './BuiltinChart';
import type { BuiltinChartRequest } from './viz/request';

/** The most points a line draws, whatever its width. */
const MAX_LINE_POINTS = 1000;

function LineChartRenderer({ component }: { component: LineChartComponent }) {
  const { dataset, ready, xKey, yKey, xLabel, yLabel } = useXYChart(component);
  const { width } = useChartFit();

  // Group rows by the x-axis category and sum the y-values, so each category
  // renders one point reflecting its total (re-grouped whenever either axis
  // changes). Also coerces y-values to numbers for a correct numeric domain.
  // Unsorted, a line runs in X-axis order (dates by date), as it does when
  // Server aggregation groups it; a chosen sort orders it instead.
  const summed = aggregateByCategory(
    dataset?.rows,
    component.xAxisKey,
    component.yAxisKey,
  );
  const chartRows =
    component.sort === 'none'
      ? inAxisOrder(summed, xKey)
      : sortCategories(
          summed,
          component.xAxisKey,
          component.yAxisKey,
          component.sort,
        );
  // About one point per pixel: a line of thousands of points looks the same
  // drawn from its outline, and draws in a fraction of the time.
  const plotted = downsampleSeries(
    chartRows,
    yKey,
    Math.max(64, Math.min(MAX_LINE_POINTS, plotWidth(width))),
  );
  const request: BuiltinChartRequest = {
    kind: 'line',
    options: {
      data: plotted,
      x: xKey,
      y: yKey,
      // A discrete axis in the order the rows are in (X-axis order, or the
      // chosen sort).
      xType: 'ordinal',
      keepOrder: true,
      xTitle: xLabel,
      yTitle: yLabel,
      interpolate: component.curve,
      filled: component.filled,
      point: component.showDots && showsPointMarkers(plotted.length),
      // Round ticks around the data, not from zero.
      zero: false,
      valueFormat: reportNumberFormatOptions(component.valueFormat),
      grid: component.showGrid,
    },
  };

  return (
    <ChartFrame
      ready={ready}
      hasData={dataset !== null}
      icon={<ChartLineUpIcon className="h-8 w-8 text-muted-foreground/40" />}
    >
      <BuiltinChart request={request} />
    </ChartFrame>
  );
}

function LineChartConfigForm({
  component,
  onUpdate,
}: {
  component: LineChartComponent;
  onUpdate: (updated: LineChartComponent) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-4">
      <CategoryValueFields
        component={component}
        onUpdate={onUpdate}
        xLabel={gt('X-axis')}
        yLabel={gt('Y-axis')}
      />
      <ConfigRow label={gt('Curve')} htmlFor="line-curve">
        <Select
          value={component.curve}
          onValueChange={(v) => {
            const curve =
              v === 'linear'
                ? ('linear' as const)
                : v === 'step'
                  ? ('step' as const)
                  : ('monotone' as const);
            onUpdate({ ...component, curve });
          }}
        >
          <ConfigSelectTrigger id="line-curve">
            <SelectValue />
          </ConfigSelectTrigger>
          <SelectContent>
            <SelectItem value="monotone">
              <T>Smooth</T>
            </SelectItem>
            <SelectItem value="linear">
              <T>Linear</T>
            </SelectItem>
            <SelectItem value="step">
              <T>Step</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </ConfigRow>
      <CategorySortFields
        idPrefix="line"
        sort={component.sort}
        unsortedLabel={gt('In X-axis order', {
          context: 'chart category sort',
        })}
        onChange={(next) => onUpdate({ ...component, sort: next.sort })}
      />
      <ConfigRow label={gt('Values')}>
        <NumberFormatFields
          idPrefix="line-values"
          value={component.valueFormat}
          onChange={(valueFormat) => onUpdate({ ...component, valueFormat })}
        />
      </ConfigRow>
      <ToggleRow
        label={gt('Filled area')}
        id="line-filled"
        checked={component.filled}
        onCheckedChange={(v) => onUpdate({ ...component, filled: v })}
      />
      <ToggleRow
        label={gt('Show dots')}
        id="line-dots"
        checked={component.showDots}
        onCheckedChange={(v) => onUpdate({ ...component, showDots: v })}
      />
      <ToggleRow
        label={gt('Show grid lines')}
        id="line-grid"
        checked={component.showGrid}
        onCheckedChange={(v) => onUpdate({ ...component, showGrid: v })}
      />
    </div>
  );
}

export function LineChartPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <line
          x1="7"
          y1="4"
          x2="7"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="7"
          y1="30"
          x2="40"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <path
          d="M 7 26 C 13 26 13 16 19 16 C 25 16 25 21 31 19 C 37 17 37 9 40 10"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 7 26 C 13 26 13 16 19 16 C 25 16 25 21 31 19 C 37 17 37 9 40 10 L 40 30 L 7 30 Z"
          fill="currentColor"
          fillOpacity="0.08"
        />
      </svg>
    </div>
  );
}

export const lineChartDefinition = defineComponentType<LineChartComponent>({
  type: 'line-chart',
  label: 'Line / area chart',
  defaultTitle: valueByCategoryTitle(
    'xAxisKey',
    'yAxisKey',
    'Line / area chart',
  ),
  description:
    'Dataset-bound line (or filled area) chart: xAxisKey column on x, summed numeric yAxisKey per x value; set filled=true for an area chart; sort none runs in x-axis order (numbers, then dates oldest first, then text); valueFormat as on the bar chart.',
  columnRefFields: ['xAxisKey', 'yAxisKey'],
  version: 1,
  make: (id) => ({
    type: 'line-chart',
    id,
    xAxisKey: null,
    yAxisKey: null,
    filled: false,
    curve: 'monotone',
    showDots: true,
    showGrid: true,
    sort: 'none',
  }),
  Renderer: LineChartRenderer,
  ConfigForm: LineChartConfigForm,
  Preview: LineChartPalettePreview,
});
